import React, { useMemo, useRef } from 'react';
import { Modal, Box } from '@cloudscape-design/components';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ModuleRegistry,
} from 'ag-grid-community';
import { RowGroupingModule, ServerSideRowModelModule } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getControlPivotData } from 'src/api/hooks/getControlPivotData';

ModuleRegistry.registerModules([RowGroupingModule, ServerSideRowModelModule]);

interface PivotTableModalProps {
  controlId: string;
  filters: Record<string, string[]>;
  visible: boolean;
  onDismiss: () => void;
}

export const PivotTableModal = ({
  controlId,
  filters,
  visible,
  onDismiss,
}: PivotTableModalProps) => {
  const isInitialMount = useRef(true);

  // TODO: add pivot column defs to ControlAttributesMetadata
  const columnDefs: ColDef[] = [
    { field: 'costCenter', rowGroup: true },
    {
      field: 'account',
      rowGroup: true,
      valueGetter: function (params) {
        const accountNumber = params.data.accountNumber;
        const accountDescription = params.data.accountDescription;
        return `${accountNumber} - ${accountDescription}`;
      },
    },
  ];

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        // skip loading the data on component mount
        if (isInitialMount.current) {
          isInitialMount.current = false;
          return;
        }

        // call the API
        getControlPivotData(controlId, filters, [], '', [], params.request.groupKeys)
          .then((response) => {
            if (params.request.groupKeys.length === 0) {
              params.success({
                rowData: response.rowData,
                rowCount: response.rowData.length,
                pivotResultFields: response.pivotResultFields,
              });
            } else {
              params.success({
                rowData: response.rowData,
                rowCount: response.rowData.length,
              });
            }
          })
          .catch((error) => {
            console.error('Error fetching data:', error);
          });
      },
    }),
    [filters]
  );

  return (
    <Modal visible={visible} onDismiss={onDismiss} header="Pivot Table View" size="max">
      <Box padding="l">
        <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
          <AgGridReact
            columnDefs={columnDefs}
            rowModelType="serverSide"
            serverSideDatasource={datasource}
            pivotMode={true}
            serverSidePivotResultFieldSeparator={'##'}
          />
        </div>
      </Box>
    </Modal>
  );
};
